import { useState, useEffect, useCallback, useRef } from "react";
import { getValidCenter, createDefaultPolygon } from "../utils/mapUtils";

export const useFarmMapModal = ({
  open,
  center,
  location,
  path,
  setPath,
  setUserCenter,
  setCurrentLocation,
  isEdit,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [manualLat, setManualLat] = useState("");
  const [manualLng, setManualLng] = useState("");
  const [forceRecenter, setForceRecenter] = useState(false);
  const [customCenter, setCustomCenter] = useState(null);
  const [useCustomCoordinates, setUseCustomCoordinates] = useState(false);
  const [isPolygonSaved, setIsPolygonSaved] = useState(false);
  const [gpsLocation, setGpsLocation] = useState(null);
  const [walkingTabState, setWalkingTabState] = useState({
    path: [],
    center: null,
  });
  const [boundaryTabState, setBoundaryTabState] = useState({
    path: [],
    center: null,
  });
  const centerUpdateTimeoutRef = useRef(null);
  const lastBoundaryUpdateRef = useRef(Date.now());

  useEffect(() => {
    if (!open) return;

    const watchId = navigator.geolocation?.watchPosition(
      (position) => {
        const gpsCoords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        };
        setGpsLocation(gpsCoords);
        if (activeTab === 0) setCurrentLocation(gpsCoords);
      },
      (error) => {
        console.error("GPS tracking error:", error);
        const fallback = { latitude: 22.862299, longitude: 77.604459 };
        setGpsLocation(fallback);
        if (activeTab === 0) setCurrentLocation(fallback);
      },
      { enableHighAccuracy: true, maximumAge: 30000, timeout: 15000 }
    );

    return () => watchId && navigator.geolocation.clearWatch(watchId);
  }, [open, activeTab, setCurrentLocation]);

  // Debounced center update for boundary tab
  const debouncedSetCenter = useCallback(
    (newCenter) => {
      if (centerUpdateTimeoutRef.current) {
        clearTimeout(centerUpdateTimeoutRef.current);
      }
      centerUpdateTimeoutRef.current = setTimeout(() => {
        if (activeTab === 1) {
          // Only for boundary tab
          const now = Date.now();
          if (now - lastBoundaryUpdateRef.current > 1000) {
            // Throttle updates
            setCustomCenter(newCenter);
            setUserCenter(newCenter);
            lastBoundaryUpdateRef.current = now;
          }
        }
      }, 300);
    },
    [activeTab, setUserCenter]
  );

  useEffect(() => {
    if (!open) return;

    if (isEdit && path && path.length > 3) {
      setIsPolygonSaved(true);
      setActiveTab(1);
    }

    const validCenter = getValidCenter(customCenter, center, location);
    const gpsCenter =
      gpsLocation?.latitude && gpsLocation?.longitude
        ? { lat: gpsLocation.latitude, lng: gpsLocation.longitude }
        : location?.latitude && location?.longitude
          ? { lat: location.latitude, lng: location.longitude }
          : validCenter;

    setWalkingTabState({
      path: [gpsCenter],
      center: gpsCenter,
    });

    const existingPath = path && path.length > 0 ? [...path] : [];
    let boundaryCenter = validCenter;
    let boundaryPath = existingPath;

    if (existingPath.length > 0) {
      const latSum = existingPath.reduce((sum, point) => sum + point.lat, 0);
      const lngSum = existingPath.reduce((sum, point) => sum + point.lng, 0);
      boundaryCenter = {
        lat: latSum / existingPath.length,
        lng: lngSum / existingPath.length,
      };

      if (isEdit) {
        setManualLat(boundaryCenter.lat.toFixed(6));
        setManualLng(boundaryCenter.lng.toFixed(6));
      }
    } else if (!isEdit) {
      const defaultPolygon = createDefaultPolygon(gpsCenter.lat, gpsCenter.lng);
      boundaryPath = defaultPolygon;
      boundaryCenter = gpsCenter;
      setPath(defaultPolygon);
    }

    setBoundaryTabState({ path: boundaryPath, center: boundaryCenter });

    if (activeTab === 1 && boundaryPath.length > 0) {
      debouncedSetCenter(boundaryCenter);
      setPath(boundaryPath);

      if (gpsLocation?.latitude && gpsLocation?.longitude) {
        setCurrentLocation(gpsLocation);
      } else {
        setCurrentLocation({
          latitude: boundaryCenter.lat,
          longitude: boundaryCenter.lng,
        });
      }
    } else if (!customCenter) {
      setCustomCenter(validCenter);
    }
  }, [
    open,
    gpsLocation?.latitude,
    gpsLocation?.longitude,
    location?.latitude,
    location?.longitude,
    isEdit,
    path?.length,
  ]);

  // Reduced frequency polygon updates for boundary tab
  useEffect(() => {
    if (open && path && path.length > 3 && activeTab === 1) {
      const now = Date.now();
      if (now - lastBoundaryUpdateRef.current > 500) {
        // Throttle updates
        const latSum = path.reduce((sum, point) => sum + point.lat, 0);
        const lngSum = path.reduce((sum, point) => sum + point.lng, 0);
        const polygonCenter = {
          lat: latSum / path.length,
          lng: lngSum / path.length,
        };

        setBoundaryTabState({ path: [...path], center: polygonCenter });

        if (gpsLocation?.latitude && gpsLocation?.longitude) {
          setCurrentLocation(gpsLocation);
        } else {
          setCurrentLocation({
            latitude: polygonCenter.lat,
            longitude: polygonCenter.lng,
          });
        }
        lastBoundaryUpdateRef.current = now;
      }
    }
  }, [open, path?.length, activeTab, gpsLocation]);

  useEffect(() => {
    if (!forceRecenter) return;

    const validCenter = getValidCenter(customCenter, center, location);
    setCustomCenter(validCenter);
    setUserCenter(validCenter);

    if (activeTab === 1 && gpsLocation?.latitude && gpsLocation?.longitude) {
      setCurrentLocation(gpsLocation);
    } else {
      setCurrentLocation({
        latitude: validCenter.lat,
        longitude: validCenter.lng,
      });
    }

    if (activeTab === 1) {
      const existingPath = boundaryTabState.path || [];
      if (existingPath.length > 0) {
        const currentCenter = {
          lat:
            existingPath.reduce((sum, point) => sum + point.lat, 0) /
            existingPath.length,
          lng:
            existingPath.reduce((sum, point) => sum + point.lng, 0) /
            existingPath.length,
        };
        const latDiff = validCenter.lat - currentCenter.lat;
        const lngDiff = validCenter.lng - currentCenter.lng;
        const newPath = existingPath.map((point) => ({
          lat: point.lat + latDiff,
          lng: point.lng + lngDiff,
        }));
        setPath(newPath);
        setBoundaryTabState({ path: newPath, center: validCenter });
      }
    }
    setForceRecenter(false);
  }, [forceRecenter, activeTab]);

  const handleTabChange = (event, newValue) => {
    if (newValue === 0 && isPolygonSaved) return;

    if (centerUpdateTimeoutRef.current) {
      clearTimeout(centerUpdateTimeoutRef.current);
    }

    const currentValidCenter = getValidCenter(customCenter, center, location);

    if (activeTab === 0) {
      setWalkingTabState({ path: [...path], center: currentValidCenter });
    } else {
      setBoundaryTabState({ path: [...path], center: currentValidCenter });
    }

    setActiveTab(newValue);

    if (newValue === 0) {
      // Walking tab logic - preserve existing behavior
      const gpsCenter =
        gpsLocation?.latitude && gpsLocation?.longitude
          ? { lat: gpsLocation.latitude, lng: gpsLocation.longitude }
          : currentValidCenter;

      const walkingPath =
        walkingTabState.path && walkingTabState.path.length > 1
          ? walkingTabState.path
          : [gpsCenter];

      setPath(walkingPath);
      setUserCenter(gpsCenter);
      setCustomCenter(gpsCenter);
      setManualLat("");
      setManualLng("");

      if (gpsLocation) setCurrentLocation(gpsLocation);
      setForceRecenter(true);
    } else {
      const centerPoint =
        boundaryTabState.center ||
        (gpsLocation?.latitude && gpsLocation?.longitude
          ? { lat: gpsLocation.latitude, lng: gpsLocation.longitude }
          : currentValidCenter);

      if (boundaryTabState.path && boundaryTabState.path.length > 0) {
        setPath(boundaryTabState.path);
      } else {
        const defaultPolygon = createDefaultPolygon(
          centerPoint.lat,
          centerPoint.lng
        );
        setPath(defaultPolygon);
        setBoundaryTabState({ path: defaultPolygon, center: centerPoint });
      }

      setUserCenter(centerPoint);
      setCustomCenter(centerPoint);

      if (gpsLocation?.latitude && gpsLocation?.longitude) {
        setCurrentLocation(gpsLocation);
      } else {
        setCurrentLocation({
          latitude: centerPoint.lat,
          longitude: centerPoint.lng,
        });
      }
      if (!boundaryTabState.path || boundaryTabState.path.length === 0) {
        setForceRecenter(true);
      }
    }
  };

  const handleSave = (handleClose) => {
    if (path.length < 3) {
      alert("Please create a valid polygon with at least 3 points");
      return;
    }

    let finalCenter = customCenter;
    if (path.length > 0) {
      const latSum = path.reduce((sum, point) => sum + point.lat, 0);
      const lngSum = path.reduce((sum, point) => sum + point.lng, 0);
      finalCenter = { lat: latSum / path.length, lng: lngSum / path.length };
    }

    setIsPolygonSaved(true);
    handleClose(finalCenter);
  };

  const handleModalClose = (handleClose) => {
    handleClose(null);
  };

  const addVertexToWalkingTab = () => {
    const walkingLocation = gpsLocation || location;
    if (!walkingLocation?.latitude || !walkingLocation?.longitude) return;

    const vertex = {
      lat: walkingLocation.latitude,
      lng: walkingLocation.longitude,
    };
    const newPath = [...path, vertex];
    setPath(newPath);
    setWalkingTabState((prev) => ({ ...prev, path: newPath }));
  };

  const applyManualCoordinates = () => {
    if (!manualLat || !manualLng) return;

    const lat = parseFloat(manualLat);
    const lng = parseFloat(manualLng);
    if (isNaN(lat) || isNaN(lng)) return;

    const newCenter = { lat, lng };
    setCustomCenter(newCenter);
    setUserCenter(newCenter);

    const existingPath = boundaryTabState.path || [];
    const hasExistingPolygon = existingPath.length > 4;

    if (!hasExistingPolygon) {
      const newPolygon = createDefaultPolygon(lat, lng);
      setPath(newPolygon);
      setBoundaryTabState({ path: newPolygon, center: newCenter });
    } else {
      setPath(existingPath);
    }

    if (gpsLocation?.latitude && gpsLocation?.longitude) {
      setCurrentLocation(gpsLocation);
    } else {
      setCurrentLocation({ latitude: lat, longitude: lng });
    }

    setForceRecenter(true);
  };

  const resetCurrentTab = () => {
    const currentGpsCenter =
      gpsLocation?.latitude && gpsLocation?.longitude
        ? { lat: gpsLocation.latitude, lng: gpsLocation.longitude }
        : {
            lat: location?.latitude || 22.862299,
            lng: location?.longitude || 77.604459,
          };

    if (activeTab === 0) {
      const newPath = [currentGpsCenter];
      setPath(newPath);
      setCustomCenter(currentGpsCenter);
      setUserCenter(currentGpsCenter);
      setWalkingTabState({ path: newPath, center: currentGpsCenter });
      setManualLat("");
      setManualLng("");
      if (gpsLocation) setCurrentLocation(gpsLocation);
    } else {
      setManualLat("");
      setManualLng("");

      const newPolygon = createDefaultPolygon(
        currentGpsCenter.lat,
        currentGpsCenter.lng
      );
      setBoundaryTabState({ path: newPolygon, center: currentGpsCenter });
      setPath(newPolygon);
      setCustomCenter(currentGpsCenter);
      setUserCenter(currentGpsCenter);

      if (gpsLocation?.latitude && gpsLocation?.longitude) {
        setCurrentLocation(gpsLocation);
      } else {
        setCurrentLocation({
          latitude: currentGpsCenter.lat,
          longitude: currentGpsCenter.lng,
        });
      }
    }
    setForceRecenter(true);
  };

  useEffect(() => {
    return () => {
      if (centerUpdateTimeoutRef.current) {
        clearTimeout(centerUpdateTimeoutRef.current);
      }
    };
  }, []);

  return {
    activeTab,
    manualLat,
    manualLng,
    forceRecenter,
    customCenter,
    useCustomCoordinates,
    isPolygonSaved,
    gpsLocation,
    setManualLat,
    setManualLng,
    setUseCustomCoordinates,
    handleTabChange,
    handleSave,
    handleModalClose,
    addVertexToWalkingTab,
    applyManualCoordinates,
    resetCurrentTab,
    getValidCenter: () => getValidCenter(customCenter, center, location),
  };
};
